import { useAsesoriasResueltas, usePendientesVerificacion } from '@/hooks';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { OficialPrefix } from '@/components/ui/OficialHint';
import { Tooltip } from '@/components/ui/Tooltip';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { CheckCircle, Headphones, Info, Paperclip, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AsesoriaPendiente } from '@/types/types';

type RevisionTab = 'pendientes' | 'rechazadas' | 'empresas-con-rechazos';

function formatDate(d?: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

const REVISION_TABS: { key: RevisionTab; label: string }[] = [
  { key: 'pendientes', label: 'Pendientes' },
  { key: 'rechazadas', label: 'Rechazadas' },
  { key: 'empresas-con-rechazos', label: 'Empresas con corrección pendiente' },
];

interface EmpresaConRechazo {
  nit: string;
  nombre: string;
  asesorNombre: string;
  cantidadRechazadas: number;
  fechaMasAntigua: string | null;
}

/** Agrupa por empresa las asesorías con corrección pendiente (RECHAZADA = REQUIERE_CORRECCIONES
 * en backend). Excluye REVERTIDA a propósito: es un retiro voluntario del asesor, no un rechazo
 * activo del admin que requiera seguimiento. */
function agruparEmpresasConRechazos(rechazadas: AsesoriaPendiente[]): EmpresaConRechazo[] {
  const soloRechazadas = rechazadas.filter((p) => p.estadoRevision === 'RECHAZADA' || p.estado === 'REQUIERE_CORRECCIONES');
  const porEmpresa = new Map<string, EmpresaConRechazo>();
  for (const p of soloRechazadas) {
    const nit = p.empresa.nitId;
    const existente = porEmpresa.get(nit);
    const fecha = p.fechaFin ?? p.fechaInicio;
    if (existente) {
      existente.cantidadRechazadas += 1;
      if (fecha && (!existente.fechaMasAntigua || fecha < existente.fechaMasAntigua)) {
        existente.fechaMasAntigua = fecha;
      }
    } else {
      porEmpresa.set(nit, {
        nit,
        nombre: p.empresa.nombre,
        asesorNombre: p.asesor.nombre,
        cantidadRechazadas: 1,
        fechaMasAntigua: fecha ?? null,
      });
    }
  }
  return Array.from(porEmpresa.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/** Cuenta cadenas (no filas crudas): un reemplazo de evidencia/audio es un borrado lógico,
 * así que sin agrupar por cadena el conteo incluiría versiones viejas ya superadas. */
function contarArchivos(p: AsesoriaPendiente): { evidencias: number; audios: number } {
  const evidencias = agruparCadenaArchivos(
    p.evidenciasGenerales.filter((e) => e.estado !== 'ELIMINADO'),
    (e) => e.evidenciaId,
    (e) => e.reemplazaEvidenciaId ?? null
  ).length;
  const audios = agruparCadenaArchivos(
    p.audios,
    (a) => a.audioId,
    (a) => a.reemplazaAudioId ?? null
  ).length;
  return { evidencias, audios };
}

type RevisionColKey = 'empresa' | 'nit' | 'asesor' | 'fechas' | 'horas' | 'difHorasPorc' | 'cumplimiento' | 'archivos' | 'acciones';

const REVISION_COLS: ColDef<RevisionColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'fechas', label: 'Fechas', defaultVisible: true },
  { key: 'horas', label: 'Horas (Antes → Después)', defaultVisible: true },
  { key: 'difHorasPorc', label: 'Diferencia Horas (%)', defaultVisible: true },
  { key: 'cumplimiento', label: 'Cumplimiento (Antes → Después)', defaultVisible: true },
  { key: 'archivos', label: 'Evidencias / Audios', defaultVisible: true },
  { key: 'acciones', label: 'Acciones', defaultVisible: true },
];

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

function ProgBar({ value, color, tooltipLabel }: { value: number; color: string; tooltipLabel?: string }) {
  const bar = (
    <span className="inline-flex items-center gap-1.5 w-full max-w-30">
      <span className="admin-prog-bar bg-white border border-gray-200 flex-1">
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[11px] font-semibold text-gray-700 whitespace-nowrap">{value.toFixed(0)}%</span>
    </span>
  );
  return tooltipLabel ? (
    <Tooltip label={tooltipLabel} className="w-full max-w-30">
      {bar}
    </Tooltip>
  ) : (
    bar
  );
}

/** Chip de estado para filas resueltas (Aprobadas/Rechazadas). "Devuelta" cubre tanto
 * REQUIERE_CORRECCIONES (estado backend) como RECHAZADA (estadoRevision mapeado); "Revertida"
 * es el caso REVERTIDA (asesoría que se le quitó la aprobación después de dada). */
function chipEstadoResuelta(p: AsesoriaPendiente): { text: string; variant: BadgeVariant } {
  if (p.estadoRevision === 'REVERTIDA' || p.estado === 'REVERTIDA') {
    return { text: 'Revertida', variant: 'gray' };
  }
  if (p.estadoRevision === 'RECHAZADA' || p.estado === 'REQUIERE_CORRECCIONES') {
    return { text: 'Devuelta', variant: 'warning' };
  }
  return { text: 'Aprobada', variant: 'green' };
}

/** Vista de la tab "Empresas con corrección pendiente": una fila por empresa (no por asesoría),
 * para ver de un vistazo qué empresas/asesores tienen correcciones sin resolver sin entrar a cada
 * asesoría. Navega al detalle de la EMPRESA (`/empresas/{nit}`, aterriza en "Estado Actual Normas"),
 * ya que `/revision/{asesoriaId}` no resuelve asesorías fuera de EN_REVISION. */
function EmpresasConRechazosCard({ empresas, onVerEmpresa }: { empresas: EmpresaConRechazo[]; onVerEmpresa: (nit: string) => void }) {
  return (
    <div className="admin-card overflow-hidden">
      <div className="admin-card-header flex items-center justify-between gap-3 border-b border-(--admin-border) px-4 py-3">
        <div className="text-[14px] font-bold text-gray-900 flex items-center gap-2">
          Empresas con corrección pendiente
          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[11px] font-semibold border border-gray-200">
            {empresas.length}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">Empresa</th>
              <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">NIT</th>
              <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">Asesor</th>
              <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">Asesorías con corrección</th>
              <th className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">Desde</th>
              <th className="text-right py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {empresas.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400 text-[13px]">
                  <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-30 text-gray-400" />
                  Ninguna empresa tiene correcciones pendientes sin resolver.
                </td>
              </tr>
            ) : (
              empresas.map((e) => (
                <tr key={e.nit} className="hover:bg-gray-50/50 cursor-pointer" onClick={() => onVerEmpresa(e.nit)}>
                  <td className="py-3 px-4 text-[13px] font-semibold text-gray-900">{e.nombre}</td>
                  <td className="py-3 px-4 text-[12px] font-mono text-gray-400">{e.nit}</td>
                  <td className="py-3 px-4 text-[13px] text-gray-600">{e.asesorNombre}</td>
                  <td className="py-3 px-4">
                    <span className="admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red) font-semibold">
                      {e.cantidadRechazadas} sin resolver
                    </span>
                  </td>
                  <td className="py-3 px-4 text-[12px] text-gray-500">{formatDate(e.fechaMasAntigua)}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="admin-btn text-[11px] font-bold py-1 px-3 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onVerEmpresa(e.nit);
                      }}
                    >
                      Ver empresa
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RevisionRowSkeleton({ colCount }: { colCount: number }) {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <tr key={i} className="animate-pulse border-b border-gray-100 last:border-b-0">
          <td colSpan={colCount} className="py-5 px-4">
            <div className="flex flex-col gap-2.5">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-3.5 bg-gray-100 rounded w-1/2" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

export function AdminRevisionPage() {
  const { pendientes, isLoading, error, refetch } = usePendientesVerificacion();
  const { resueltas, isLoading: resueltasLoading, error: resueltasError, load: loadResueltas } = useAsesoriasResueltas();
  const navigate = useNavigate();
  const showSecretPanel = useAdminSecretPanel();
  const [filterText, setFilterText] = useState('');
  const [activeTab, setActiveTab] = useState<RevisionTab>('pendientes');
  const [resueltasLoadedOnce, setResueltasLoadedOnce] = useState(false);

  useEffect(() => {
    loadResueltas().then(() => setResueltasLoadedOnce(true));
  }, [loadResueltas]);

  const {
    visible: colVisible,
    toggle: toggleCol,
    reset: resetCols,
  } = useColumnVisibility<RevisionColKey>(REVISION_COLS, 'admin_revision_cols');

  const colCount = useMemo(() => Object.values(colVisible).filter(Boolean).length, [colVisible]);

  const handleTabClick = (tab: RevisionTab) => {
    setActiveTab(tab);
    if (tab !== 'pendientes') {
      loadResueltas().then(() => setResueltasLoadedOnce(true));
    }
  };

  const pendientesFiltradas = useMemo(() => {
    return pendientes.filter((p) => p.estadoRevision !== 'APROBADA' && p.estado !== 'FINALIZADA');
  }, [pendientes]);

  const rechazadas = useMemo(
    () => resueltas.filter((p) => p.estadoRevision === 'RECHAZADA' || p.estadoRevision === 'REVERTIDA'),
    [resueltas]
  );
  const empresasConRechazos = useMemo(() => agruparEmpresasConRechazos(rechazadas), [rechazadas]);
  // Se calcula de `resueltas`, que ya se carga siempre al montar la página (línea de abajo),
  // sin importar la tab activa — así el conteo de rechazos por empresa está disponible incluso
  // mirando la tab "Pendientes".
  const rechazosPorEmpresa = useMemo(() => new Map(empresasConRechazos.map((e) => [e.nit, e.cantidadRechazadas])), [empresasConRechazos]);

  // Para mostrar "NIT - Nombre" en el indicador de filtro activo cuando el filtro es justo el NIT
  // de una empresa (p.ej. al hacer clic en la etiqueta de rechazos) — un NIT solo no dice mucho.
  const nombrePorNit = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of [...pendientes, ...resueltas]) m.set(p.empresa.nitId, p.empresa.nombre);
    return m;
  }, [pendientes, resueltas]);

  const NOMBRE_EMPRESA_MAX_CHARS = 40;
  const filtroActivoLabel = useMemo(() => {
    const nombre = nombrePorNit.get(filterText);
    if (!nombre) return filterText;
    const nombreCorto = nombre.length > NOMBRE_EMPRESA_MAX_CHARS ? `${nombre.slice(0, NOMBRE_EMPRESA_MAX_CHARS)}...` : nombre;
    return `${filterText} - ${nombreCorto}`;
  }, [filterText, nombrePorNit]);

  const sourceList = activeTab === 'pendientes' ? pendientesFiltradas : activeTab === 'rechazadas' ? rechazadas : [];

  const filtered = useMemo(() => {
    return sourceList.filter((p) => {
      const q = filterText.toLowerCase();
      return (
        p.empresa.nombre.toLowerCase().includes(q) || p.empresa.nitId.toLowerCase().includes(q) || p.asesor.nombre.toLowerCase().includes(q)
      );
    });
  }, [sourceList, filterText]);

  const grouped = useMemo(() => {
    const groupsMap = new Map<string, typeof filtered>();
    filtered.forEach((p) => {
      const nit = p.empresa.nitId;
      if (!groupsMap.has(nit)) {
        groupsMap.set(nit, []);
      }
      groupsMap.get(nit)!.push(p);
    });

    for (const [, list] of groupsMap.entries()) {
      list.sort((a, b) => {
        const orderA = a.orden ?? a.asesoriaId;
        const orderB = b.orden ?? b.asesoriaId;
        return orderA - orderB;
      });
    }

    return Array.from(groupsMap.values());
  }, [filtered]);

  const handleRowClick = (p: AsesoriaPendiente) => {
    const isPending =
      p.estadoRevision === 'PENDIENTE' && p.estado !== 'FINALIZADA' && p.estado !== 'REQUIERE_CORRECCIONES' && p.estado !== 'REVERTIDA';

    if (isPending) {
      navigate(`/revision/${p.asesoriaId}`);
    } else {
      navigate(`/empresas/${p.empresa.nitId}/asesorias/${p.asesoriaId}`);
    }
  };

  if (error) {
    return (
      <div className="p-12 text-center text-(--admin-red) bg-red-50/50 rounded-xl border border-red-100/50">
        <p className="font-semibold mb-2">Error al cargar pendientes</p>
        <p className="text-[12px] opacity-80 mb-4">{error}</p>
        <button type="button" className="admin-btn primary justify-center" onClick={refetch}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-['DM_Sans'] animate-in fade-in-50 duration-200">
      {/* Search and Filters Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 border border-(--admin-border) rounded-xl shadow-xs">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por empresa, NIT o asesor..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="admin-search-input pl-9 pr-8 w-full text-[13px]"
          />
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              title="Quitar filtro"
              className="absolute right-2.5 top-1.5 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 self-end sm:self-auto">
          {filterText ? (
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-(--admin-blue) bg-(--admin-blue-bg) px-2 py-1 rounded-md border border-(--admin-blue)/20">
              Filtro activo: "{filtroActivoLabel}"
              <button type="button" onClick={() => setFilterText('')} className="hover:text-gray-700 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ) : (
            <span className="text-[11px] text-gray-400 font-medium">Sin filtro</span>
          )}
          <span className="text-[12px] text-gray-500 font-medium">
            {activeTab === 'empresas-con-rechazos'
              ? `${empresasConRechazos.length} empresa${empresasConRechazos.length !== 1 ? 's' : ''} con corrección pendiente`
              : `${filtered.length} de ${sourceList.length} asesoría${sourceList.length !== 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Tabs de estado de revisión */}
      <div className="flex items-center gap-2">
        {REVISION_TABS.map((tab) => {
          const count =
            tab.key === 'pendientes'
              ? pendientes.length
              : !resueltasLoadedOnce
                ? null
                : tab.key === 'rechazadas'
                  ? rechazadas.length
                  : empresasConRechazos.length;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className={`admin-btn text-[12px] font-bold py-1.5 px-3 border transition-colors ${
                isActive
                  ? 'active bg-(--admin-blue) text-white border-(--admin-blue)'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
              {count != null && (
                <span
                  className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    isActive ? 'bg-black text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Main Table view */}
      {activeTab === 'empresas-con-rechazos' ? (
        <EmpresasConRechazosCard empresas={empresasConRechazos} onVerEmpresa={(nit) => navigate(`/empresas/${nit}`)} />
      ) : (
        <div className="admin-card overflow-hidden">
          <div className="admin-card-header flex items-center justify-between gap-3 border-b border-(--admin-border) px-4 py-3">
            <div className="text-[14px] font-bold text-gray-900 flex items-center gap-2">
              Revisión de Asesorías
              <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[11px] font-semibold border border-gray-200">
                {filtered.length}
              </span>
            </div>
            <ColumnPicker cols={REVISION_COLS} visible={colVisible} onToggle={toggleCol} onReset={resetCols} activeCount={colCount} />
          </div>

          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="bg-gray-50/50">
                  {REVISION_COLS.filter((c) => colVisible[c.key]).map((c) => (
                    <th key={c.key} className="text-left py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading || (activeTab !== 'pendientes' && resueltasLoading) ? (
                  <RevisionRowSkeleton colCount={colCount} />
                ) : activeTab !== 'pendientes' && resueltasError ? (
                  <tr>
                    <td colSpan={colCount} className="text-center py-12 text-(--admin-red) text-[13px]">
                      {resueltasError}
                    </td>
                  </tr>
                ) : grouped.length > 0 ? (
                  grouped.map((group) => {
                    const company = group[0].empresa;
                    const rechazosEmpresa = rechazosPorEmpresa.get(company.nitId) ?? 0;
                    // Aunque estas asesorías ya se pueden REVISAR, no se pueden APROBAR mientras una
                    // anterior de la misma empresa siga en corrección (REQUIERE_CORRECCIONES/REVERTIDA)
                    // sin resolver — el admin no lo ve porque esa anterior vive en la tab "Rechazadas",
                    // no en "Pendientes". Un solo aviso arriba del grupo, no repetido por fila.
                    const primeraDelGrupo = group[0];
                    const bloqueanteGrupo = rechazadas
                      .filter(
                        (r) =>
                          r.empresa.nitId === primeraDelGrupo.empresa.nitId &&
                          (r.orden ?? r.asesoriaId) < (primeraDelGrupo.orden ?? primeraDelGrupo.asesoriaId)
                      )
                      .sort((a, b) => (b.orden ?? b.asesoriaId) - (a.orden ?? a.asesoriaId))[0];
                    const labelBloqueanteGrupo = bloqueanteGrupo
                      ? bloqueanteGrupo.orden === 1
                        ? 'Diagnóstico'
                        : bloqueanteGrupo.orden != null
                          ? `Asesoría #${bloqueanteGrupo.orden - 1}`
                          : `Asesoría #${bloqueanteGrupo.asesoriaId}`
                      : null;
                    return (
                      <React.Fragment key={company.nitId}>
                        {/* Fila de título del grupo (Empresa) */}
                        <tr className="bg-gray-50/40 border-b border-gray-100">
                          <td colSpan={colCount} className="py-2.5 px-4 text-[13px] font-bold text-gray-900">
                            <div className="flex items-center justify-between gap-3">
                              <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                {company.nombre}
                              </span>
                              <span className="flex items-center gap-3">
                                <span className="font-mono text-[11px] text-gray-400 font-normal">NIT: {company.nitId}</span>
                                {rechazosEmpresa > 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTabClick('rechazadas');
                                      setFilterText(company.nitId);
                                    }}
                                    className="admin-chip text-[10px] bg-(--admin-red-bg) text-(--admin-red) font-semibold hover:brightness-95 transition-all"
                                    title="Ver las asesorías con corrección pendiente de esta empresa"
                                  >
                                    {rechazosEmpresa} rechazo{rechazosEmpresa > 1 ? 's' : ''}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/empresas/${company.nitId}`);
                                  }}
                                  className="admin-btn text-[11px] font-bold py-1 px-3 border border-gray-200 transition-colors bg-white hover:bg-gray-50 text-gray-700"
                                >
                                  Ver empresa
                                </button>
                              </span>
                            </div>
                          </td>
                        </tr>
                        {labelBloqueanteGrupo && bloqueanteGrupo && (
                          <tr
                            className="bg-amber-50/60 border-b border-amber-100 hover:bg-amber-100/60 transition-colors cursor-pointer"
                            onClick={() => handleRowClick(bloqueanteGrupo)}
                            title={`Ir a ${labelBloqueanteGrupo}`}
                          >
                            <td colSpan={colCount} className="py-1.5 px-4 pl-6">
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-800 font-semibold group-hover:underline">
                                <Info className="w-3.5 h-3.5 shrink-0 text-amber-600" />
                                {labelBloqueanteGrupo} requiere correcciones — debe resolverse antes de poder aprobar las asesorías
                                siguientes.
                              </span>
                            </td>
                          </tr>
                        )}
                        {/* Filas de las asesorías del grupo */}
                        {group.map((p) => {
                          const cambio =
                            (p.resumenEvaluacion?.despues?.cumplimiento ?? 0) - (p.resumenEvaluacion?.antes?.cumplimiento ?? 0);
                          const label =
                            p.orden === 1 ? 'Diagnóstico' : p.orden != null ? `Asesoría #${p.orden - 1}` : `Asesoría #${p.asesoriaId}`;
                          return (
                            <React.Fragment key={p.asesoriaId}>
                              <tr
                                className="border-b border-gray-100 last:border-b-0 transition-colors hover:bg-gray-50/50 clickable"
                                onClick={() => handleRowClick(p)}
                              >
                                {colVisible.empresa && (
                                  <td className="py-3 px-4 pl-6 text-gray-900 font-semibold max-w-xs truncate">
                                    <div className="flex flex-col gap-0.5">
                                      <span className="text-[13px] text-gray-900">{label}</span>
                                      <span className="text-[11px] text-gray-500 font-normal line-clamp-1">{p.objetivo}</span>
                                    </div>
                                  </td>
                                )}
                                {colVisible.nit && (
                                  <td className="py-3 px-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-1">
                                      <span className="font-mono text-[11px] text-gray-400">{p.empresa.nitId}</span>
                                      {activeTab === 'pendientes' ? (
                                        // Las marcadas autoAprobar ya no llegan aquí — el backend
                                        // las excluye de obtenerAsesoriasPendientes por estar
                                        // resueltas de cara al admin (preaprobación transparente).
                                        <span className="text-[9px] text-green-600 font-semibold bg-green-50 border border-green-100 rounded px-1.5 py-0.5 w-fit">
                                          Disponible para revisión
                                        </span>
                                      ) : (
                                        (() => {
                                          const chip = chipEstadoResuelta(p);
                                          return (
                                            <Badge variant={chip.variant} className="text-[9px] w-fit">
                                              {chip.text}
                                            </Badge>
                                          );
                                        })()
                                      )}
                                    </div>
                                  </td>
                                )}
                                {colVisible.asesor && (
                                  <td className="py-3 px-4 text-gray-600 text-[13px] whitespace-nowrap">{p.asesor.nombre}</td>
                                )}
                                {colVisible.fechas && (
                                  <td className="py-3 px-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-0.5 text-[12px]">
                                      <div className="text-gray-700 font-medium">
                                        <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wide mr-1">Asesoría</span>
                                        {formatDate(p.fechaInicio)}
                                      </div>
                                      <div className="text-gray-700 font-medium">
                                        <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wide mr-1">Enviada</span>
                                        {p.fechaFin ? (
                                          formatDate(p.fechaFin)
                                        ) : p.estado === 'EN_CURSO' ? (
                                          <span className="text-gray-400 font-normal italic">En curso</span>
                                        ) : (
                                          // fechaFin nula con un estado que ya no es EN_CURSO (ej. EN_REVISION) no
                                          // debería pasar en el flujo normal — decir "En curso" aquí sería falso.
                                          // Caso real: una edición directa fuera de finalizarAsesoriaActiva dejó el
                                          // estado en EN_REVISION sin setear fechaFin.
                                          <span className="text-gray-400 font-normal italic">Sin fecha registrada</span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                )}
                                {colVisible.horas && (
                                  <td className="py-3 px-4 text-gray-600 text-[12px] whitespace-nowrap">
                                    {/* El acumulado antes->después ya suma la cadena completa de anteriores por
                                    orden, sin importar si el admin las aprobó — igual que el cumplimiento, no
                                    hay que esperar a que estén FINALIZADA para que sus horas cuenten. */}
                                    {p.horasAntes != null && p.horasDespues != null ? (
                                      <div className="flex items-center gap-1.5">
                                        <OficialPrefix oficial={p.horasOficial} proyectado={p.horasAntes} format={formatHours} />
                                        <Tooltip label="Horas acumuladas antes de esta asesoría (proyectado)" align="left">
                                          <span className="text-gray-400">{formatHours(p.horasAntes)}</span>
                                        </Tooltip>
                                        <span className="text-gray-400">→</span>
                                        <Tooltip label="Horas acumuladas después de esta asesoría (proyectado)" align="center">
                                          <span className="font-semibold text-(--admin-green)">{formatHours(p.horasDespues)}</span>
                                        </Tooltip>
                                      </div>
                                    ) : (
                                      <span title="Duración de esta asesoría">{formatHours(p.horas)}</span>
                                    )}
                                  </td>
                                )}
                                {colVisible.difHorasPorc && (
                                  <td className="py-3 px-4 text-gray-600 text-[12px] font-semibold whitespace-nowrap">
                                    {p.porcentajeHorasAntes != null && p.porcentajeHorasDespues != null ? (
                                      (() => {
                                        const dif = p.porcentajeHorasDespues - p.porcentajeHorasAntes;
                                        return (
                                          <Tooltip
                                            label="Diferencia de horas respecto a la meta de 320h, antes vs. después de esta asesoría"
                                            align="right"
                                          >
                                            <span className={dif > 0 ? 'text-(--admin-green)' : 'text-gray-500'}>
                                              {dif > 0 ? `+${dif.toFixed(1)}%` : `${dif.toFixed(1)}%`}
                                            </span>
                                          </Tooltip>
                                        );
                                      })()
                                    ) : (
                                      <span className="text-gray-300 italic">—</span>
                                    )}
                                  </td>
                                )}
                                {colVisible.cumplimiento && (
                                  <td className="py-3 px-4 whitespace-nowrap">
                                    {p.resumenEvaluacion ? (
                                      <div className="flex items-center gap-2">
                                        <OficialPrefix
                                          oficial={p.resumenEvaluacion.antes.cumplimientoOficial}
                                          proyectado={p.resumenEvaluacion.antes.cumplimiento}
                                          format={(n) => `${n.toFixed(1)}%`}
                                        />
                                        <Tooltip label="Cumplimiento antes de esta asesoría (proyectado)">
                                          <span className="text-gray-400 text-[11px]">
                                            {p.resumenEvaluacion.antes.cumplimiento.toFixed(0)}%
                                          </span>
                                        </Tooltip>
                                        <span className="text-gray-400 text-[11px]">→</span>
                                        <ProgBar
                                          value={p.resumenEvaluacion.despues.cumplimiento}
                                          color={pColor(p.resumenEvaluacion.despues.cumplimiento)}
                                          tooltipLabel="Cumplimiento después de esta asesoría (proyectado)"
                                        />
                                        {cambio !== 0 && (
                                          <Tooltip label="Cambio de cumplimiento que propone esta asesoría">
                                            <span
                                              className={`text-[10px] font-bold ${cambio > 0 ? 'text-(--admin-green)' : 'text-(--admin-red)'}`}
                                            >
                                              ({cambio > 0 ? `+${cambio.toFixed(1)}%` : `${cambio.toFixed(1)}%`})
                                            </span>
                                          </Tooltip>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-300 italic text-[11px]">—</span>
                                    )}
                                  </td>
                                )}
                                {colVisible.archivos &&
                                  (() => {
                                    const { evidencias, audios } = contarArchivos(p);
                                    return (
                                      <td className="py-3 px-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3 text-[12px] text-gray-600">
                                          <span className="inline-flex items-center gap-1" title="Evidencias">
                                            <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                                            {evidencias}
                                          </span>
                                          <span className="inline-flex items-center gap-1" title="Audios">
                                            <Headphones className="w-3.5 h-3.5 text-gray-400" />
                                            {audios}
                                          </span>
                                        </div>
                                      </td>
                                    );
                                  })()}
                                {colVisible.acciones && (
                                  <td className="py-3 px-4 text-right whitespace-nowrap">
                                    <button
                                      type="button"
                                      className="admin-btn text-[11px] font-bold py-1 px-3 border border-gray-200 transition-colors bg-white hover:bg-gray-50 text-gray-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRowClick(p);
                                      }}
                                    >
                                      {activeTab !== 'pendientes' ? 'Ver' : 'Revisar'}
                                    </button>
                                  </td>
                                )}
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={colCount} className="text-center py-12 text-gray-400 text-[13px]">
                      <CheckCircle className="w-8 h-8 mx-auto mb-3 opacity-30 text-gray-400" />
                      No hay asesorías {activeTab === 'pendientes' ? 'pendientes' : activeTab} de revisión que coincidan con tu búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
